import { useCallback, useEffect, useState } from 'react';

import {
  Box,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  startOfMonth,
  startOfYear,
  endOfYear,
  subDays,
  subYears,
} from 'date-fns';
import { AppBarWithApiRef } from '@Components/app-bar';
import { paymentColumns } from './columns';
import Table from '@Components/table';
import useTableRequestParams from 'hooks/useTableRequestParams';
import useActionColumn from '@Components/table/useActionColumn';
import { Modal } from '@Components/modal';
import useModal from 'hooks/useModal';
import { RefundPayment } from './RefundPayment';
import { useReactToPrint } from 'react-to-print';
import Papa from 'papaparse';
import {
  useLazyPaymentsQuery,
  usePaymentsBySelectedIdMutation,
  usePaymentsQuery,
  useRefundPaymentMutation,
} from '@Store/api/paymentsSlice';
import { exportData } from '@Utils/export-csv';
import { IPayment } from '@Types/Payment';
import { handleFetchError } from '@Utils/handleError';
import { toast } from 'react-toastify';

const Container = styled(Box)`
  padding: 0px;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

type ExportTimePeriod =
  | 'TODAY'
  | 'YESTERDAY'
  | 'THIS_WEEK'
  | 'LAST_WEEK'
  | 'THIS_MONTH'
  | 'LAST_MONTH'
  | 'THIS_YEAR'
  | 'LAST_YEAR'
  | 'CUSTOM';

// All dates stored in the DB (PaymentHistoryEntity.CreatedAt) are UTC, so these ranges are
// computed as UTC-bound instants - startOfDay/endOfDay/etc. below operate in local time and
// .toISOString() converts that local start/end instant to its UTC equivalent, so the backend
// always receives a correct UTC boundary regardless of the admin's browser timezone.
function getExportDateRange(period: ExportTimePeriod): { startDate: Date; endDate: Date } {
  const now = new Date();

  switch (period) {
    case 'TODAY':
      return { startDate: startOfDay(now), endDate: endOfDay(now) };
    
      case 'YESTERDAY': {
      const yesterday = subDays(now, 1);

      return { startDate: startOfDay(yesterday), endDate: endOfDay(yesterday) };
    }
    case 'THIS_WEEK':
      return { startDate: startOfWeek(now), endDate: endOfDay(now) };
    
      case 'LAST_WEEK': {
      const lastWeekStart = startOfWeek(subDays(now, 7));
      const lastWeekEnd = endOfDay(new Date(lastWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000));

      return { startDate: lastWeekStart, endDate: lastWeekEnd };
    }
    case 'THIS_MONTH':
      return { startDate: startOfMonth(now), endDate: endOfDay(now) };
    
      case 'LAST_MONTH': {
      const lastMonthStart = startOfMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      const lastMonthEnd = endOfDay(new Date(now.getFullYear(), now.getMonth(), 0));

      return { startDate: lastMonthStart, endDate: lastMonthEnd };
    }
    case 'THIS_YEAR':
      return { startDate: startOfYear(now), endDate: endOfDay(now) };
    
      case 'LAST_YEAR': {
      const lastYear = subYears(now, 1);

      return { startDate: startOfYear(lastYear), endDate: endOfYear(lastYear) };
    }
    default:
      return { startDate: startOfDay(now), endDate: endOfDay(now) };
  }
}

const EXPORT_TIME_PERIOD_LABELS: Record<ExportTimePeriod, string> = {
  TODAY: 'Today',
  YESTERDAY: 'Yesterday',
  THIS_WEEK: 'This Week',
  LAST_WEEK: 'Last Week',
  THIS_MONTH: 'This Month',
  LAST_MONTH: 'Last Month',
  THIS_YEAR: 'This Year',
  LAST_YEAR: 'Last Year',
  CUSTOM: 'Custom Range',
};

// date input fields take/return YYYY-MM-DD strings interpreted in the browser's local timezone.
function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}


export function PaymentHistory() {
  const tableParams = useTableRequestParams();

  const { data: payments, error: tableDataError } =
    usePaymentsQuery(tableParams);

  const [fetchSelectedPayments] = usePaymentsBySelectedIdMutation();

  const [getAllPayments] = useLazyPaymentsQuery();

  const [refundPayment] = useRefundPaymentMutation();

  const [exportDateDialogOpen, setExportDateDialogOpen] = useState(false);
  const [exportTimePeriod, setExportTimePeriod] =
    useState<ExportTimePeriod>('THIS_MONTH');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');

  const {
    isOpen: isRefundModalOpen,
    item: refundItem,
    openModal: openRefundModal,
    closeModal: closeRefundModal,
  } = useModal<IPayment>();

  const handleRefund = async () => {
    try {
      const _response = await refundPayment({
        transactionId: refundItem!.transactionId,
        amount: refundItem.amount,
      }).unwrap();
      closeRefundModal();
      toast.success('Payment was successfully reversed');
    } catch (err: any) {
      const message =
        err?.data?.message || err?.message || 'Something went wrong...';
      toast.error(message);
    }
  };

  const handleOpenRefundPayment = useCallback(
    (payment: IPayment) => {
      openRefundModal(payment);
    },
    [openRefundModal]
  );

  const actionColumn = useActionColumn({
    handleRefund: handleOpenRefundPayment,
    shouldDisableRefund: (row) => row.status !== 'CAPTURED',
  });

  const handlePrint = useReactToPrint({
    content: () => document.getElementById('payment-container'),
  });

  const exportRows = (rows: IPayment[]) => {
    const dataToExport = rows.map((data) => [
      ...paymentColumns.map((column) => column.valueGetter!({ row: data } as any)),
    ]);

    const csv = Papa.unparse({
      fields: paymentColumns.map((column) => column.headerName!),
      data: dataToExport,
    });

    exportData(csv);
  };

  const handleExport = async (selectedIds?: string[]) => {
    if (selectedIds) {
      const paymentsToExport = await fetchSelectedPayments(
        selectedIds
      ).unwrap();

      exportRows(paymentsToExport);
      
      return;
    }

    // Exporting the full, unpaged result set requires a bounded date range (enforced server-side
    // too) so this can't accidentally try to pull the entire payment history table at once.
    // Default to "This Month" so the dialog opens with a sensible range already selected.
    const defaultPeriod: ExportTimePeriod = 'THIS_MONTH';
    const defaultRange = getExportDateRange(defaultPeriod);

    setExportTimePeriod(defaultPeriod);
    setExportStartDate(formatDateInputValue(defaultRange.startDate));
    setExportEndDate(formatDateInputValue(defaultRange.endDate));
    setExportDateDialogOpen(true);
  };

  const handleExportDateDialogCancel = () => {
    setExportDateDialogOpen(false);
  };

  const handleExportTimePeriodChange = (event: SelectChangeEvent<ExportTimePeriod>) => {
    const period = event.target.value as ExportTimePeriod;

    setExportTimePeriod(period);

    if (period !== 'CUSTOM') {
      const range = getExportDateRange(period);

      setExportStartDate(formatDateInputValue(range.startDate));
      setExportEndDate(formatDateInputValue(range.endDate));
    }
  };

  const handleExportDateDialogConfirm = async () => {
    if (!exportStartDate || !exportEndDate) {
      toast.error('Please select both a start and end date.');

      return;
    }

    try {
      // exportStartDate/exportEndDate are date-only strings (YYYY-MM-DD) from the date inputs.
      // Parsing with a "T00:00:00" suffix anchors them to local midnight - new Date('YYYY-MM-DD')
      // instead parses as UTC midnight, which would silently shift the range by a timezone offset.
      // startOfDay/endOfDay give an inclusive whole-day range in local time, and toISOString()
      // converts that local instant to the UTC value the backend expects (CreatedAt is stored UTC).
      const parsedStartDate = startOfDay(new Date(`${exportStartDate}T00:00:00`));
      const parsedEndDate = endOfDay(new Date(`${exportEndDate}T00:00:00`));

      if (parsedStartDate > parsedEndDate) {
        toast.error('Start date must be before the end date.');

        return;
      }

      const paymentsToExport = await getAllPayments({
        getAllPages: true,
        startDate: parsedStartDate.toISOString(),
        endDate: parsedEndDate.toISOString(),
      } as any).unwrap();

      setExportDateDialogOpen(false);
      exportRows(paymentsToExport?.items || []);
    } catch (err: any) {
      const message =
        err?.data?.message || err?.message || 'Something went wrong...';
      toast.error(message);
    }
  };

  useEffect(() => {
    handleFetchError(tableDataError);
  }, [tableDataError]);

  return (
    <Container id='payment-container'>
      <Table
        checkboxSelection
        printable
        searchable
        columns={[...paymentColumns, actionColumn]}
        handleExport={handleExport}
        handlePrint={handlePrint}
        paginated={payments?.items.length ? payments.items.length > 0 : false}
        rows={payments?.items || []}
        searchOnSubmit
        toolbar={AppBarWithApiRef}
        toolbarTitle='Payment History'
        totalCount={payments?.count}
      />
      {isRefundModalOpen ? (
        <Modal
          handleClose={closeRefundModal}
          label={'Refund'}
          open={isRefundModalOpen}
        >
          <RefundPayment payment={refundItem} onRefund={handleRefund} />
        </Modal>
      ) : null}

      <Dialog
        fullWidth
        maxWidth='xs'
        onClose={handleExportDateDialogCancel}
        open={exportDateDialogOpen}
      >
        <DialogTitle>Select Export Date Range</DialogTitle>
        <DialogContent>
          <Typography color='text.secondary' sx={{ mb: 2 }} variant='body2'>
            Exporting the full payment history requires a date range.
          </Typography>
          <Box display='flex' flexDirection='column' gap={2}>
            <FormControl fullWidth>
              <InputLabel id='export-time-period-label'>Date Range</InputLabel>
              <Select
                label='Date Range'
                labelId='export-time-period-label'
                onChange={handleExportTimePeriodChange}
                value={exportTimePeriod}
              >
                {Object.entries(EXPORT_TIME_PERIOD_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              InputLabelProps={{ shrink: true }}
              disabled={exportTimePeriod !== 'CUSTOM'}
              label='Start Date'
              onChange={(e) => setExportStartDate(e.target.value)}
              type='date'
              value={exportStartDate}
            />
            <TextField
              InputLabelProps={{ shrink: true }}
              disabled={exportTimePeriod !== 'CUSTOM'}
              label='End Date'
              onChange={(e) => setExportEndDate(e.target.value)}
              type='date'
              value={exportEndDate}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExportDateDialogCancel}>Cancel</Button>
          <Button onClick={handleExportDateDialogConfirm} variant='contained'>
            Export
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
